import { Component, EventEmitter, OnInit, Output, QueryList, ViewChild, ViewChildren, OnDestroy } from '@angular/core';
import { TributoPeriodo } from '../../../models/enums/tributosPeriodo.enum';
import { JwtHelper } from '../../../helpers/jwtDecode.helper';
import { Icones } from '../../../shared/variaveisDeRotas';
import { retornaDescricao } from '../../../shared/mock/descricao';
import { Table } from 'primeng/table';
import { Imposto } from '../../../models/tributos.model';
import { TributosService } from '../../../services/tributos.service';
import { SpinnerService } from '../../../services/spinner.service';
import { ToastService } from '../../../services/toast.service';
import { SeveridadeToast } from '../../../models/enums/severidade-toast.enum';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'app-tributos-pagar',
    templateUrl: './tributos-pagar.component.html',
    styleUrls: ['./tributos-pagar.component.scss']
})
export class TributosPagarComponent implements OnInit, OnDestroy {

    tributos: Imposto[] = [];
    tributosFiltrados: Imposto[] = [];
    gruposTributos: { titulo: string; tributos: Imposto[] }[] = [];

    @ViewChild('dt') dt: Table | undefined;
    @ViewChildren('dt') tables!: QueryList<Table>;

    @Output() tituloChange = new EventEmitter<string>();
    @Output() iconeChange = new EventEmitter<string>();
    @Output() tituloDescricao = new EventEmitter<string>();
    @Output() textoDescricao = new EventEmitter<string>();
    @Output() mostrarDescricao = new EventEmitter<boolean>();

    private userId: string;
    private destroy$ = new Subject<void>();
    filtroTipoSelecionado: TributoPeriodo | null = null;
    carregando: boolean = false;
    loading: boolean = false;
    abaAtiva: number = 0;
    globalFilters: string[] = ['imposto', 'competencia', 'vencimento', 'valor'];

    tiposConfirmacaoPagamento = [
        { label: 'Todos', value: null },
        { label: 'Pendente', value: 0 },
        { label: 'Pago', value: 1 }
    ];

    constructor(
        private jwtHelper: JwtHelper,
        private tributosService: TributosService,
        private spinnerService: SpinnerService,
        private toastService: ToastService
    ) {
        this.userId = this.jwtHelper.getUserIdFromToken();
    }

    ngOnInit(): void {
        this.tituloChange.emit('Tributos a Pagar');
        this.iconeChange.emit(Icones.listaTopicoAmarelo);
        this.tituloDescricao.emit(retornaDescricao('tributos').titulo);
        this.textoDescricao.emit(retornaDescricao('tributos').texto);
        this.mostrarDescricao.emit(true);
        this.carregarTributos();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.mostrarDescricao.emit(false);
    }

    carregarTributos(): void {
        this.loading = true;
        this.spinnerService.show('Carregando tributos...');

        this.tributosService.listarPorUsuario(this.userId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (data) => {
                    this.tributos = data;
                    this.tributosFiltrados = [...this.tributos];
                    this.separarPorStatus();
                    this.loading = false;
                },
                error: (error) => {
                    this.loading = false;
                    this.toastService.setMensagemToast('Falha ao carregar tributos.', SeveridadeToast.Error);
                    console.error('Erro ao carregar tributos:', error);
                },
                complete: () => {
                    this.spinnerService.hide();
                }
            });
    }

    separarPorStatus(): void {
        const hoje = new Date();
        hoje.setHours(0, 0, 0, 0);

        const aVencer: Imposto[] = [];
        const atrasados: Imposto[] = [];

        for (const imposto of this.tributosFiltrados) {
            if (imposto.confirmacaoPagamento !== 0) continue;
            
            const dataVenc = this.converterVencimentoParaDate(imposto.vencimento);
 
            dataVenc.setHours(0, 0, 0, 0);
            if (dataVenc >= hoje) {
                aVencer.push(imposto);
            } else {
                atrasados.push(imposto);
            }
        }

        this.gruposTributos = [
            { titulo: 'A vencer', tributos: aVencer },
            { titulo: 'Atrasado', tributos: atrasados }
        ];
    }

    filtrarPorTipo(): void {
        const impostosFiltrados = this.tributos.filter(imposto => imposto.confirmacaoPagamento === 0);

        const filtro = this.filtroTipoSelecionado;

        const hoje = new Date();
        hoje.setHours(0, 0, 0, 0);

        const aPagar = impostosFiltrados.filter(imposto => {
            const dataVenc = this.converterVencimentoParaDate(imposto.vencimento);
            dataVenc.setHours(0, 0, 0, 0);
            const estaAVencer = dataVenc >= hoje;
            
            return estaAVencer && (filtro === null || imposto.confirmacaoPagamento === filtro);
        });

        this.tributosFiltrados = aPagar;
        this.separarPorStatus();
    }

    confirmarPagamento(tributoLinha: Imposto): void {
        this.carregando = true;
        this.spinnerService.show('Atualizando status...');

        const novoStatus = tributoLinha.confirmacaoPagamento === 0 ? 1 : 0;
        const dataPagamento = novoStatus === 1 ? new Date() : null;

        this.tributosService.atualizarStatusPagamento({
            id: tributoLinha.id!,
            confirmacaoPagamento: novoStatus,
            dataPagamento: dataPagamento
        })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
            next: () => {
                tributoLinha.confirmacaoPagamento = novoStatus;
                tributoLinha.dataPagamento = dataPagamento?.toISOString();
                this.carregando = false;
                this.toastService.setMensagemToast('Status atualizado com sucesso!', SeveridadeToast.Success);
                this.separarPorStatus();
            },
            error: (error) => {
                this.carregando = false;
                this.toastService.setMensagemToast('Falha ao atualizar status.', SeveridadeToast.Error);
                console.error('Erro ao atualizar status:', error);
            },
            complete: () => {
                this.spinnerService.hide();
            }
        });
    }

    baixarArquivo(tributo: Imposto): void {
        if (!tributo.id) return;

        this.spinnerService.show('Baixando arquivo...');

        this.tributosService.baixarArquivo(tributo.id)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (blob) => {
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = tributo.nomeArquivo || 'arquivo';
                    link.click();
                    window.URL.revokeObjectURL(url);
                    this.toastService.setMensagemToast('Arquivo baixado com sucesso!', SeveridadeToast.Success);
                },
                error: (error) => {
                    this.toastService.setMensagemToast('Falha ao baixar arquivo.', SeveridadeToast.Error);
                    console.error('Erro ao baixar arquivo:', error);
                },
                complete: () => {
                    this.spinnerService.hide();
                }
            });
    }

    converterDataStringParaDate(dataString: string): Date {
        if (!dataString) return new Date();
        
        const [dia, mes, ano] = dataString.split('/');
        return new Date(parseInt(ano, 10), parseInt(mes, 10) - 1, parseInt(dia, 10));
    }

    converterVencimentoParaDate(vencimento: string | Date): Date {
        if (!vencimento) return new Date();
        
        if (typeof vencimento === 'string') {

            if (vencimento.includes('T') || vencimento.includes('-')) {
                return new Date(vencimento);
            } else {
                return this.converterDataStringParaDate(vencimento);
            }
        } else {
            return new Date(vencimento);
        }
    }

    formatarDataExibicao(data: string | Date): string {
        if (!data) return '';
        
        if (data instanceof Date) {
            const dia = data.getDate().toString().padStart(2, '0');
            const mes = (data.getMonth() + 1).toString().padStart(2, '0');
            const ano = data.getFullYear();
            return `${dia}/${mes}/${ano}`;
        }
        
        if (typeof data === 'string') {
            try {
                const dataObj = new Date(data);
                if (!isNaN(dataObj.getTime())) {
                    const dia = dataObj.getDate().toString().padStart(2, '0');
                    const mes = (dataObj.getMonth() + 1).toString().padStart(2, '0');
                    const ano = dataObj.getFullYear();
                    return `${dia}/${mes}/${ano}`;
                }
            } catch (e) {
            }
        }
        
        return data;
    }

    formatarValor(valor: string | number): string {
        if (!valor) return 'R$ 0,00';
        
        const numValor = typeof valor === 'string' ? parseFloat(valor) : valor;
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(numValor);
    }

    applyFilterGlobal(value: string): void {
        if (this.dt) {
            this.dt.filterGlobal(value, 'contains');
        }
    }

    applyFilterAba(event: Event, idx: number): void {
        const input = event.target as HTMLInputElement;
        const filterValue = input.value;
        const tablesArr = this.tables?.toArray();
        if (tablesArr && tablesArr[idx]) {
            tablesArr[idx].filterGlobal(filterValue, 'contains');
        }
    }

    customSort(event: any) {
        event.data.sort((data1: any, data2: any) => {
            let value1 = data1[event.field];
            let value2 = data2[event.field];

            if (event.field === 'vencimento') {
                value1 = this.converterVencimentoParaDate(value1);
                value2 = this.converterVencimentoParaDate(value2);
            }
            if (event.field === 'competencia') {
                const [m1, y1] = value1.split('/');
                const [m2, y2] = value2.split('/');
                value1 = new Date(+('20' + y1), +m1 - 1, 1);
                value2 = new Date(+('20' + y2), +m2 - 1, 1);
            }

            let result = 0;
            if (value1 == null && value2 != null)
                result = -1;
            else if (value1 != null && value2 == null)
                result = 1;
            else if (value1 == null && value2 == null)
                result = 0;
            else if (value1 < value2)
                result = -1;
            else if (value1 > value2)
                result = 1;
            else
                result = 0;

            return (event.order * result);
        });
    }
} 
